/**
 * Venue websites are typed by hand and imported from spreadsheets, so they arrive in every
 * shape: `https://x.fr`, `www.x.fr`, `x.fr`, `//x.fr`, sometimes with stray whitespace. A bare
 * `www.x.fr` in an `href` (or `window.open`) is a *relative* path — the browser resolves it
 * against the app's own origin and lands on a 404 instead of the venue's site.
 */

/** Anything that looks like `scheme:` at the very start. */
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Normalize a stored URL into something safe to put in an `href` or hand to `window.open`.
 * Returns `null` when there is nothing usable, so callers can skip rendering the link.
 */
export function externalUrl(raw: string | null | undefined): string | null {
  const url = raw?.trim();
  if (!url) return null;

  // Already absolute http(s) — use as typed.
  if (/^https?:\/\//i.test(url)) return url;

  // Any other scheme (`javascript:`, `data:`, `mailto:`, ...) is not a website: refuse it
  // rather than prefixing it into something nonsensical.
  if (SCHEME_RE.test(url)) return null;

  // Protocol-relative (`//x.fr`) and schemeless (`www.x.fr`, `x.fr/page`) both just need a scheme.
  return `https://${url.replace(/^\/\//, '')}`;
}

/** The URL as shown to the user: no scheme, no `www.`, no trailing slash. */
export function displayUrl(raw: string | null | undefined): string {
  return (raw ?? '')
    .trim()
    .replace(/^(https?:)?\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');
}
